/**
 * Which bridge this membership talks to.
 *
 * ── Why the app has to ask ──────────────────────────────────────────────────
 * The bridge is being replaced per OWNER: some accounts move to the Laravel
 * app, everyone else stays on Lumen until it retires. This app cannot make that
 * call itself — it picks its API base before login, and nothing it holds then
 * identifies the owner. The community token in the URL belongs to the project,
 * and `getmembership` answers with funnel_id, codes and branding but no
 * user_id. So it asks the admin app, which already serves this frontend and can
 * resolve token → funnels.user_id.
 *
 * The answer is a URL, not an owner id: the pilot list lives on the server, so
 * widening or aborting it is a config change there rather than a deploy of
 * this bundle — which goes live for every customer domain at once.
 *
 * ── Resolved before anything else runs ──────────────────────────────────────
 * index.js awaits this before the first render, so every request in a session
 * goes to ONE host. That matters beyond tidiness: a session that logged in
 * against one bridge and then called the other would be carrying a token the
 * second may not accept. Cached per token in localStorage, so only a first
 * visit pays the round trip.
 *
 * ── Failing safe ────────────────────────────────────────────────────────────
 * Every failure — network, timeout, malformed answer, an unrecognised URL —
 * falls back to the default bridge, which is where everyone runs today. The
 * worst outcome of this whole mechanism going wrong is the status quo.
 */
import axios from "axios";

/** The bridge everyone used before this existed, and the fallback for anything
 *  that goes wrong. Mirrors the defaults in config.js. */
export const DEFAULT_BASE =
  process.env.REACT_APP_COMMUNITY_API_URL ||
  (process.env.NODE_ENV === "production"
    ? "https://bridge.flexifunnels.com/api"
    : "http://flexiapi.local.com/api");

/**
 * Bases this app will accept from the resolver.
 *
 * The server is trusted, but an answer that is not one of these means something
 * is misconfigured, and silently pointing the whole app at an arbitrary origin
 * is not a failure mode worth having. Anything else falls back.
 */
const ALLOWED = [
  DEFAULT_BASE,
  process.env.REACT_APP_PILOT_API_URL ||
    (process.env.NODE_ENV === "production"
      ? "https://api-v2.flexifunnels.com/api"
      : "http://flexiapiv2.local.com/api"),
];

/** How long a resolved base is reused before asking again. */
const TTL_MS = 6 * 60 * 60 * 1000;

/** Longest the first render will wait. Past this, start on the default. */
const TIMEOUT_MS = 2000;

let resolved = null;

/** The community token from /membership/:token/… — same shape axiosSetup uses. */
export function communityTokenFromPath() {
  if (typeof window === "undefined") return null;
  const p = window.location.pathname;
  const m = p.match(/^\/membership\/([^/]+)/) || p.match(/^\/([^/]+)/);
  return m ? m[1] : null;
}

const cacheKey = (token) => `api_base:${token}`;

function readCache(token) {
  try {
    const raw = localStorage.getItem(cacheKey(token));
    if (!raw) return null;
    const { base, at } = JSON.parse(raw);
    if (!base || Date.now() - at > TTL_MS) return null;
    return ALLOWED.includes(base) ? base : null;
  } catch (e) {
    return null; // private mode, blocked storage, corrupt entry
  }
}

function writeCache(token, base) {
  try {
    localStorage.setItem(cacheKey(token), JSON.stringify({ base, at: Date.now() }));
  } catch (e) {
    /* nothing to do — the next load just asks again */
  }
}

/** The base to use right now. Always a usable URL, resolved or not. */
export function apiBase() {
  return resolved || DEFAULT_BASE;
}

/** Test seam. */
export function resetApiBase() {
  resolved = null;
}

/**
 * Settle which bridge to use. Safe to call more than once; the first answer
 * wins for the life of the page, because changing host mid-session is exactly
 * what this is designed to avoid.
 */
export async function resolveApiBase(adminBase) {
  if (resolved) return resolved;

  const token = communityTokenFromPath();
  if (!token) return DEFAULT_BASE; // not on a membership route at all

  const cached = readCache(token);
  if (cached) {
    resolved = cached;
    return resolved;
  }

  try {
    const res = await axios.post(
      `${adminBase}/membership-api-base`,
      { community_token: token },
      { timeout: TIMEOUT_MS }
    );
    const base = String(res?.data?.api_base || "");
    if (ALLOWED.includes(base)) {
      resolved = base;
      writeCache(token, base);
      return resolved;
    }
  } catch (e) {
    /* fall through to the default — see the note at the top */
  }

  return DEFAULT_BASE;
}
